// @flow

import * as React from 'react';
import {useState} from 'react';
import Avatar from 'sail/Avatar';
import Box from 'sail/Box';

import Group from 'sail/Group';
import Icon from 'sail/Icon';
import {MenuItem} from 'sail/Menu';
import MenuContainer from 'sail/MenuContainer';
import {BodyAlt} from 'sail/Text';
import TruncatedText from 'sail/TruncatedText';
import {UnstyledLink} from 'sail/UnstyledLink';
import {PressableButton, PressableContext} from 'sail/Pressable';
import {useAccount, useLocation} from 'docs/data';
import {getLoginUrl} from 'docs/util';

import type {Account, Location, Merchant} from 'docs/data';

import './AccountSwitcher.scss';

const DEFAULT_ACCOUNT_NAME = 'Unnamed account';

type SafeImageProps = {|
  src: ?string,
  fallback: React.Node,
|};

const SafeImage = ({src, fallback}: SafeImageProps) => {
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState(false);
  /**
   * We render the fallback on the server since if the image fails to load
   * while server-side rendering, `onError` doesn't get called. This means
   * our first attempt at rendering the `img` must happen on the client.
   */
  React.useEffect(() => setMounted(true), []);

  if (!mounted || !src || error) {
    return fallback;
  } else {
    return <img src={src} onError={() => setError(true)} />;
  }
};

type MerchantLinkProps = {
  merchant: Merchant,
};

const MerchantLink = ({merchant, ...linkProps}: MerchantLinkProps) => (
  <UnstyledLink {...linkProps}>
    <Group direction="horizontal" spacing={8} alignItems="center">
      <Avatar shadow="small" size={32}>
        <SafeImage src={merchant.iconUrl} fallback={<Icon icon="business" />} />
      </Avatar>
      <Box style={{width: '165px'}}>
        <BodyAlt>
          <TruncatedText>
            {merchant.nickname || DEFAULT_ACCOUNT_NAME}
          </TruncatedText>
        </BodyAlt>
      </Box>
    </Group>
  </UnstyledLink>
);

const MerchantButton = ({merchant, ...linkProps}: MerchantLinkProps) => {
  return (
    <PressableButton
      radius="all"
      style={{
        borderRadius: 24,
        boxShadow: 'none',
        backgroundColor: '#e3e8ee',
        marginRight: -4,
      }}
    >
      <PressableContext.Consumer>
        {(context) => (
          <UnstyledLink
            {...linkProps}
            className={context.pressableContextClasses}
          >
            <Group
              direction="horizontal"
              spacing={8}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Box padding={{left: 8}}>
                <BodyAlt>
                  <TruncatedText>
                    {merchant.nickname || DEFAULT_ACCOUNT_NAME}
                  </TruncatedText>
                </BodyAlt>
              </Box>
              <Avatar shadow="none" size={32}>
                <SafeImage
                  src={merchant.iconUrl}
                  fallback={<Icon icon="business" />}
                />
              </Avatar>
            </Group>
          </UnstyledLink>
        )}
      </PressableContext.Consumer>
    </PressableButton>
  );
};

type AccountSwitcherMenuProps = {|
  account: Account,
  onMerchantSelected: (merchant: Merchant) => void,
  location: Location,
|};

const AccountSwitcherMenu = ({
  account,
  onMerchantSelected,
  location,
}: AccountSwitcherMenuProps) => (
  <Box padding={{vertical: 8}} className="AccountSwitcherMenu">
    {account.availableMerchants.map((merchant) => (
      <MenuItem
        key={merchant.token}
        onClick={() => onMerchantSelected(merchant)}
      >
        <MerchantLink
          merchant={merchant}
          href={getLoginUrl({
            account,
            merchant,
            returnUrl: location.toString(),
          })}
        />
      </MenuItem>
    ))}
  </Box>
);

export const AccountSwitcher = () => {
  const [showMenu, setShowMenu] = useState(false);
  const {account} = useAccount();
  const {location} = useLocation();

  const handleMerchantSelected = (merchant: Merchant) => {
    setShowMenu(false);
  };

  const menu = (
    <AccountSwitcherMenu
      account={account}
      onMerchantSelected={handleMerchantSelected}
      location={location}
    />
  );

  if (!account.merchant?.isLoggedIn) {
    return null;
  }

  const hasMultipleAccounts = account.availableMerchants.length > 1;

  return (
    <Box>
      {hasMultipleAccounts ? (
        <MenuContainer
          contextMeetingPoint="bottomright"
          layerMeetingPoint="topright"
          contents={menu}
          autoflip="none"
          onMenuStateChange={(value) => setShowMenu(value)}
          showMenu={showMenu}
          offsetX={-19}
        >
          <MerchantButton merchant={account.merchant} />
        </MenuContainer>
      ) : (
        <MerchantButton
          merchant={account.merchant}
          style={{cursor: 'initial'}}
        />
      )}
    </Box>
  );
};
